import Reveal from './Reveal'

const services = [
  { title: 'Financial systems', desc: 'Payments, trading, reconciliation and ledgers, built for correctness under load.' },
  { title: 'Healthcare solutions', desc: 'Telehealth platforms, clinical workflow tools and prior-authorisation automation. Test environments are designed to hold no patient data.' },
  { title: 'Vibe-coded app scanning', desc: 'We review apps built fast with AI tools for security holes, broken logic and missing tests. You get a written fix list in priority order.' },
  { title: 'Productization', desc: 'We turn a working prototype into a product you can sell: tenancy, billing, access control, monitoring and deployment.' },
  { title: 'SaaS platforms', desc: 'Multi-tenant architecture, subscriptions and tenant isolation designed in from day one.' },
  { title: 'Data engineering', desc: 'Pipelines, warehousing and analytics that turn operational data into decisions.' },
  { title: 'Cloud architecture', desc: 'Infrastructure for resilience and cost control on AWS, GCP and Azure.' },
  { title: 'Full-stack product engineering', desc: 'Interface, API and database, owned end to end by one team.' },
  { title: 'Security and compliance', desc: 'Systems designed to pass audits in regulated sectors, not patched for them afterwards.' },
]

const standards = [
  { area: 'Security', items: ['OWASP ASVS 5.0', 'NIST SP 800-63B Rev. 4'] },
  { area: 'Health', items: ['ISO 27799:2016', 'IEC 81001-5-1:2021', 'HIPAA'] },
  { area: 'Privacy', items: ['GDPR', 'POPIA'] },
]

const stack = [
  { group: 'Frontend', items: 'React, TypeScript, Next.js, Angular, Tailwind CSS' },
  { group: 'Backend', items: 'Node.js, Python, FastAPI, Ruby on Rails, C# / .NET, Java EE' },
  { group: 'Data', items: 'PostgreSQL, SQL Server, MongoDB, Redis, Kafka, BigQuery' },
  { group: 'Cloud and delivery', items: 'AWS, Docker, Kubernetes, NGINX, GitLab CI/CD' },
]

export default function Services() {
  return (
    <section id="services" aria-labelledby="services-title" className="bg-parchment text-ledger pt-32 md:pt-40 pb-24 md:pb-32">
      <div className="container mx-auto px-6">
        <Reveal>
          <p className="site-label text-brass">01 · What we do</p>
          <h2 id="services-title" className="site-display text-4xl md:text-5xl mt-5 max-w-3xl">
            Eight kinds of work. One standard for all of them.
          </h2>
        </Reveal>

        <Reveal>
          <ol className="mt-16 border-t border-rule">
            {services.map((s, i) => (
              <li key={s.title} className="grid md:grid-cols-12 gap-4 md:gap-10 py-7 border-b border-rule">
                <span className="site-label text-brass md:col-span-1 pt-1">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-brandSerif font-semibold text-2xl md:col-span-4">{s.title}</h3>
                <p className="text-base leading-relaxed text-ledger/80 md:col-span-7">{s.desc}</p>
              </li>
            ))}
          </ol>
        </Reveal>

        <Reveal>
          <div className="mt-24">
            <p className="site-label text-brass">Standards we test our work against</p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-ledger/80">
              We check systems for readiness against these frameworks. Results are engineering findings, not legal opinions.
            </p>
            <dl className="mt-8 grid md:grid-cols-3 gap-x-12 gap-y-8">
              {standards.map((group) => (
                <div key={group.area} className="border-t border-rule pt-5">
                  <dt className="font-brandMono text-sm text-ledger/70">{group.area}</dt>
                  <dd className="mt-3 space-y-2">
                    {group.items.map((item) => (
                      <p key={item} className="text-base">{item}</p>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-24">
            <p className="site-label text-brass">Technologies we ship with</p>
            <dl className="mt-8 grid md:grid-cols-2 gap-x-16 gap-y-8">
              {stack.map((row) => (
                <div key={row.group} className="grid grid-cols-[9rem_1fr] gap-4 border-t border-rule pt-5">
                  <dt className="font-brandMono text-sm text-ledger/70">{row.group}</dt>
                  <dd className="text-base">{row.items}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
